import {
  alpha,
  Box,
  Button,
  IconButton,
  Link,
  styled,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router';

import { LINKS, SIZES } from '../../../../assets/constants/constants';
import Dots from '../../../../assets/images/dots_doodle.svg?styled';
import { LANG } from '../../../../services/Config';
import Logo from '../../../Logo';

import FacebookIcon from './_components/icons/FacebookIcon';
import TiktokIcon from './_components/icons/TiktokIcon';

// Styled Components

const FooterContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(0, 4, 8),
  width: '100%',
  maxWidth: SIZES.layout.width.xl,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  marginTop: '35px',
  [theme.breakpoints.up('lg')]: {
    padding: theme.spacing(8, 4),
    flexDirection: 'row',
    marginTop: 0,
  },
  [theme.breakpoints.up(1560)]: {
    padding: theme.spacing(8, 0),
    margin: theme.spacing(0, 4),
  },
  [theme.breakpoints.down('md')]: {
    marginTop: '35px',
  },
}));

const ContactSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  position: 'relative',
  zIndex: 1050,
  margin: theme.spacing(-8, 'auto', 6),
  backgroundColor: '#FFFFFF',
  color: theme.palette.text.default,
  boxShadow: theme.shadows[21],
  borderRadius: '24px',
  width: '100%',
  padding: theme.spacing(3),
  maxWidth: 330,
  [theme.breakpoints.up('lg')]: {
    margin: 0,
    order: 2,
    height: 'min-content',
  },
}));

const ContactContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  margin: theme.spacing(1.5, 0, 3),
  justifyContent: 'center',
}));

const HalfCircle = styled(Box)(({ theme }) => ({
  width: 54,
  height: 54,
  marginLeft: -27,
  marginRight: theme.spacing(1.5),
  border: `15px solid ${theme.palette.primary.light}`,
  borderRadius: '50%',
  borderBottomColor: 'transparent',
  borderLeftColor: 'transparent',
  transform: 'rotate(-315deg)',
}));

const ContactLinks = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
});

const MainSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  width: '100%',
  justifyContent: 'space-between',
  [theme.breakpoints.up('sm')]: {
    flexDirection: 'row',
  },
  paddingRight: theme.spacing(2),
  order: 1,
  textAlign: 'left',
}));

const MainColumn = styled(Box)(({ theme }) => ({
  display: 'flex',
  width: '50%',
  justifyContent: 'center',
  flexDirection: 'column',
  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '65%',
  },
}));

const LeftColumn = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
});

const StyledLogo = styled(Logo)(({ theme }) => ({
  height: 24,
  marginBottom: theme.spacing(1),
  [theme.breakpoints.up('sm')]: {
    marginBottom: theme.spacing(4),
  },
  [theme.breakpoints.up('lg')]: {
    marginBottom: theme.spacing(3),
  },
}));

const LeftTopSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  marginBottom: theme.spacing(4.5),
}));

const LeftEcoSection = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
});

const RightColumn = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
});

const RightEcoSection = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
});

const AddressColumn = styled(Box)(({ theme }) => ({
  width: '50%',
  display: 'flex',
  flexDirection: 'column',
  [theme.breakpoints.up('md')]: {
    width: '30%',
  },
}));

const EcoAddressColumn = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  marginBottom: theme.spacing(6),
}));

const TermsLinksContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  margin: theme.spacing(6, 0, 0, 0),
  [theme.breakpoints.up(820)]: {
    display: 'none',
  },
}));

const FooterBottom = styled(Box)(({ theme }) => ({
  backgroundColor: '#0C2346',
  display: 'flex',
  justifyContent: 'center',
  width: '100%',
  height: 70,
  [theme.breakpoints.up('sm')]: {
    height: 90,
  },
}));

const FooterBottomContent = styled(Box)(({ theme }) => ({
  margin: theme.spacing(0, 4),
  maxWidth: SIZES.layout.width.xl,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
  [theme.breakpoints.up(820)]: {
    justifyContent: 'space-between',
  },
}));

const FooterLinks = styled(Box)(({ theme }) => ({
  display: 'none',
  [theme.breakpoints.up(820)]: {
    display: 'flex',
    alignItems: 'center',
  },
}));

const Dot = styled(Box)(({ theme }) => ({
  backgroundColor: '#354560',
  height: 4,
  width: 4,
  margin: theme.spacing(3),
}));

const StyledDots = styled(Dots)(({ theme }) => ({
  position: 'absolute',
  height: 38,
  width: 38,
  left: '50%',
  top: -24,
  transform: 'translate(-50%, -50%) rotate(-40deg)',
  fill: theme.palette.primary.light,
  '& path': { stroke: theme.palette.primary.light },
  [theme.breakpoints.up('lg')]: {
    height: 30,
    width: 30,
    left: 'unset',
    right: -27,
    transform: 'none',
    fill: theme.palette.neutral.dark,
    '& path': { stroke: theme.palette.neutral.dark },
  },
}));

const Footer = () => {
  const { appStrings, routes } = LANG;

  const mainLinks = [
    { name: 'PRODUCTS_REFINANCING', bold: true },
    { name: 'MY_SCOOPR', bold: true },
    { name: 'CUSTOMER_SERVICE', bold: true },
  ] as const;
  const economyLinks = [
    { name: 'MY_ECONOMY', bold: true },
    { name: 'MY_DEBT', bold: false },
    { name: 'CREDIT_CHECK_MYSELF', bold: false },
    // { name: 'PARTNER_BANKS', bold: false },
    { name: 'DEBT_REGISTER', bold: false },
    { name: 'DEBT_INFORMATION', bold: false },
    { name: 'INTEREST_RATE', bold: false },
    { name: 'SMALL_LOAN', bold: false },
    { name: 'DEBT_FREE', bold: false },
    { name: 'CREDIT_CARD_LOAN', bold: false },
    { name: 'DEBT_OVERVIEW', bold: false },
    { name: 'REPAYMENT_PERIOD', bold: false },
    { name: 'REFINANCING_LOANS', bold: false },
    { name: 'SAVE_OR_REPAY_LOAN', bold: false },
    { name: 'MICROLOAN', bold: false },
    { name: 'NOMINAL_AND_EFFECTIVE_INTEREST_RATE', bold: false },
    { name: 'PROPERTY_VALUE', bold: false },
  ] as const;

  const renderLink = (url: string, title: string, bold: boolean) => {
    return (
      <Link
        component={RouterLink}
        to={url}
        sx={(theme) => ({
          color: bold
            ? theme.palette.text.footerLink
            : theme.palette.text.contrast,
          marginBottom: theme.spacing(2),
        })}
        underline="hover"
        key={title}
      >
        <Typography
          component={bold ? 'h4' : 'p'}
          variant={bold ? 'h4n' : 'body3n'}
        >
          {title}
        </Typography>
      </Link>
    );
  };

  return (
    <Box
      component="footer"
      sx={(theme) => ({
        width: '100%',
        backgroundColor: theme.palette.background.contrast,
        color: theme.palette.text.contrast,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      })}
    >
      <FooterContent>
        <ContactSection>
          <StyledDots />
          <Typography component="h2" variant="h2n">
            {appStrings.CONTACT.TITLE}
          </Typography>

          <ContactContainer>
            <HalfCircle />
            <ContactLinks>
              <Link href={`tel:${appStrings.CONTACT.PHONE2}`} underline="hover">
                <Typography component="h4" variant="h4n">
                  {appStrings.CONTACT.PHONE1}
                </Typography>
              </Link>
              <Link
                href={`mailto:${appStrings.CONTACT.EMAIL}`}
                underline="hover"
              >
                <Typography component="h4" variant="h4n">
                  {appStrings.CONTACT.EMAIL}
                </Typography>
              </Link>
            </ContactLinks>
          </ContactContainer>

          <Typography component="h4" variant="h4n">
            {appStrings.CONTACT.OPENING_TITLE}
          </Typography>
          <Box
            component="table"
            sx={(theme) => ({
              width: '100%',
              borderSpacing: 0,
              margin: theme.spacing(1, 0, 3),
              '& td': {
                padding: theme.spacing(0.5, 0),
                fontSize: '0.875rem',
              },
              [theme.breakpoints.up('sm')]: {
                '& td': {
                  fontSize: '1rem',
                },
              },
            })}
          >
            <tbody>
              {appStrings.CONTACT.OPENING.REGULAR.items.map(
                ({ day, hours }) => (
                  <tr key={day}>
                    <td>
                      <Typography component="p" variant="body3n">
                        {day}:
                      </Typography>
                    </td>
                    <td
                      style={{
                        textAlign: 'right',
                        minWidth: 80,
                        verticalAlign: 'bottom',
                      }}
                    >
                      <Typography component="h4" variant="h4n">
                        {hours}
                      </Typography>
                    </td>
                  </tr>
                ),
              )}

              {appStrings.CONTACT.OPENING.HOLIDAYS.display ? (
                <>
                  <tr>
                    <td>
                      <Typography component="p" variant="h4n" className="pt-2">
                        {appStrings.CONTACT.OPENING.HOLIDAYS.title}
                      </Typography>
                    </td>
                  </tr>
                  {appStrings.CONTACT.OPENING.HOLIDAYS.items.map(
                    ({ day, hours }) => (
                      <tr key={day}>
                        <td>
                          <Typography component="p" variant="body3n">
                            {day}:
                          </Typography>
                        </td>
                        <td
                          style={{
                            textAlign: 'right',
                            minWidth: 80,
                            verticalAlign: 'bottom',
                          }}
                        >
                          <Typography component="h4" variant="h4n">
                            {hours}
                          </Typography>
                        </td>
                      </tr>
                    ),
                  )}
                </>
              ) : null}
            </tbody>
          </Box>

          <Button
            variant="contained"
            color="primary"
            component={RouterLink}
            to={routes.CUSTOMER_SERVICE}
            sx={{ margin: 'auto' }}
          >
            <Typography component="p" variant="button2n">
              {appStrings.MENU.CUSTOMER_SERVICE}
            </Typography>
          </Button>
        </ContactSection>

        <MainSection>
          <MainColumn>
            <LeftColumn>
              <StyledLogo />
              <LeftTopSection>
                {mainLinks.map(({ name, bold }) =>
                  renderLink(routes[name], appStrings.MENU[name], bold),
                )}
              </LeftTopSection>
              <LeftEcoSection>
                {economyLinks
                  .slice(0, 6)
                  .map(({ name, bold }) =>
                    renderLink(routes[name], appStrings.MENU[name], bold),
                  )}
              </LeftEcoSection>
            </LeftColumn>

            <RightColumn>
              <RightEcoSection>
                {economyLinks
                  .slice(6, economyLinks.length - 3)
                  .map(({ name, bold }) =>
                    renderLink(routes[name], appStrings.MENU[name], bold),
                  )}
              </RightEcoSection>
              <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                <EcoAddressColumn>
                  {economyLinks
                    .slice(-3)
                    .map(({ name, bold }) =>
                      renderLink(routes[name], appStrings.MENU[name], bold),
                    )}
                </EcoAddressColumn>
              </Box>
            </RightColumn>
          </MainColumn>
          <AddressColumn>
            <Box sx={{ display: { xs: 'block', md: 'none' } }}>
              <EcoAddressColumn>
                {economyLinks
                  .slice(-3)
                  .map(({ name, bold }) =>
                    renderLink(routes[name], appStrings.MENU[name], bold),
                  )}
              </EcoAddressColumn>
            </Box>
            <Box component="address">
              <Typography variant="body3n" component="p">
                Scoopr AS
              </Typography>
              <Typography
                variant="body4n"
                component="p"
                sx={(theme) => ({
                  marginBottom: theme.spacing(2),
                })}
              >
                Org.nr. 952 660 225
              </Typography>
              <Typography variant="body4n" component="p">
                Karenslyst Allé 2
              </Typography>
              <Typography
                variant="body4n"
                component="p"
                sx={(theme) => ({
                  marginBottom: theme.spacing(2),
                })}
              >
                0278 Oslo
              </Typography>
              <IconButton
                component={Link}
                href={LINKS.FACEBOOK}
                rel="noopener noreferrer"
                aria-label={appStrings.CONTACT.FACEBOOK_LABEL}
                size="large"
                sx={(theme) => ({
                  color: theme.palette.text.contrast,
                  margin: theme.spacing(0, 1, 0, -1),
                  '&:hover': {
                    backgroundColor: alpha(
                      theme.palette.background.default,
                      0.1,
                    ),
                  },
                  '&:focus-visible': {
                    backgroundColor: alpha(
                      theme.palette.background.default,
                      0.1,
                    ),
                  },
                  '& svg': {
                    width: 30,
                    height: 30,
                  },
                  [theme.breakpoints.down('md')]: {
                    '& svg': {
                      width: 24,
                      height: 24,
                    },
                  },
                })}
              >
                <FacebookIcon />
              </IconButton>
              <IconButton
                component={Link}
                href={LINKS.TIKTOK}
                rel="noopener noreferrer"
                aria-label={appStrings.CONTACT.TIKTOK_LABEL}
                size="large"
                sx={(theme) => ({
                  color: theme.palette.text.contrast,
                  margin: theme.spacing(0, 1, 0, -1),
                  '&:hover': {
                    backgroundColor: alpha(
                      theme.palette.background.default,
                      0.1,
                    ),
                  },
                  '&:focus-visible': {
                    backgroundColor: alpha(
                      theme.palette.background.default,
                      0.1,
                    ),
                  },
                  '& svg': {
                    width: 30,
                    height: 30,
                  },
                  [theme.breakpoints.down('md')]: {
                    '& svg': {
                      width: 24,
                      height: 24,
                    },
                  },
                })}
              >
                <TiktokIcon />
              </IconButton>
            </Box>
            <TermsLinksContainer>
              <Link
                component={RouterLink}
                to={routes.TERMS_OF_USE_MIN_SCOOPR}
                sx={(theme) => ({
                  color: theme.palette.text.contrast,
                  marginBottom: theme.spacing(2),
                })}
                underline="hover"
              >
                <Typography component="p" variant="body3n">
                  {appStrings.MENU.TERMS_OF_USE}
                </Typography>
              </Link>
              <Link
                component={RouterLink}
                to={routes.PRIVACY_STATEMENT}
                sx={(theme) => ({
                  color: theme.palette.text.contrast,
                  marginBottom: theme.spacing(2),
                })}
                underline="hover"
              >
                <Typography component="p" variant="body3n">
                  {appStrings.MENU.PRIVACY}
                </Typography>
              </Link>
              <Link
                component={RouterLink}
                to={routes.COOKIES}
                sx={(theme) => ({
                  color: theme.palette.text.contrast,
                  marginBottom: theme.spacing(2),
                })}
                underline="hover"
              >
                <Typography component="p" variant="body3n">
                  {appStrings.MENU.COOKIES}
                </Typography>
              </Link>
            </TermsLinksContainer>
          </AddressColumn>
        </MainSection>
      </FooterContent>

      <FooterBottom>
        <FooterBottomContent>
          <FooterLinks>
            <Link
              component={RouterLink}
              to={routes.TERMS_OF_USE_MIN_SCOOPR}
              sx={(theme) => ({
                color: theme.palette.text.contrast,
              })}
              underline="hover"
            >
              <Typography component="p" variant="body3n">
                {appStrings.MENU.TERMS_OF_USE}
              </Typography>
            </Link>
            <Dot />
            <Link
              component={RouterLink}
              to={routes.PRIVACY_STATEMENT}
              sx={(theme) => ({
                color: theme.palette.text.contrast,
              })}
              underline="hover"
            >
              <Typography component="p" variant="body3n">
                {appStrings.MENU.PRIVACY_STATEMENT}
              </Typography>
            </Link>
            <Dot />
            <Link
              component={RouterLink}
              to={routes.COOKIES}
              sx={(theme) => ({
                color: theme.palette.text.contrast,
              })}
              underline="hover"
            >
              <Typography component="p" variant="body3n">
                {appStrings.MENU.COOKIES}
              </Typography>
            </Link>
          </FooterLinks>

          <Typography component="p" variant="body3n">
            {new Date().getFullYear()} {appStrings.RIGHTS}
          </Typography>
        </FooterBottomContent>
      </FooterBottom>
    </Box>
  );
};

export default Footer;
